import { Button, Popover, Table, TableProps, Tag } from 'antd';
import React, { useEffect, useRef, useState } from 'react'
import DiscoverBrandItemCell from './DiscoverBrandItemCell';
import Paragraph from 'antd/es/typography/Paragraph';
import dayjs from 'dayjs';
import RightArrowIcon from '../../../../components/icons/RightArrowIcon';
import { DATE_FORMAT_DD_MM_YYYY, STATUS_INTEREST_ACCEPTED, STATUS_INTEREST_EXPRESSED, STATUS_INTEREST_RECEIVED, STATUS_INTERESTS_EXPRESSED, STATUS_INTERESTS_RECEIVED, STATUS_OPEN, STATUS_PARTNER_INTEREST_ACCEPTED, STATUS_PARTNER_INTEREST_EXPRESSED, STATUS_PENDING } from '../../../../constants/constants';
import { getBrandRequests, GetBrandRequests, getBrandRequestsByOrgId, getSuitableBrandRequests } from '../../../../API/endpoints/Discovery';
import { useNavigate } from 'react-router-dom';

const PAGE_SIZE = 20;

interface Props {
    userProfile: any
}
const DiscoveryRequestsList = (props: Props) => {
    const { userProfile } = props;

    const tableRef = useRef<any>(null);

    const navigate = useNavigate();

    const [data, setData] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [page, setPage] = useState(1);

    const [scrollY, setScrollY] = useState(0);




    const loadData = async (pageNumber: number) => {
        setLoading(true);
        if (userProfile?.org_id) {
            getBrandRequests(page, PAGE_SIZE, userProfile?.org_id, userProfile?.partnerv2_id, [STATUS_OPEN, STATUS_INTEREST_EXPRESSED]).then((res) => {
                const newData = res?.data?.map((item: any) => {
                    const data = item?.discoveryRequest || item;
                    const counts = item?.counts && item?.counts?.length > 0 ? item?.counts[0] : {}
                    return {
                        id: data?.id,
                        brand_name: data?.brand?.brand_name,
                        category: data?.category,
                        subCategory: data?.sub_category,
                        intParteners: counts?.interested_count || 0,
                        updatedAt: data?.updated_at,
                        createdAt: data?.created_at,
                        status: data?.status,
                    }
                }) || []
                pageNumber == 1 ? setData(newData) : setData(prev => [...prev, ...newData]);
                const total = res?.totalCount || 0;
                setHasMore((pageNumber * PAGE_SIZE) < total);
                setLoading(false);
            }).catch((err) => {
                console.log("Failed to fetch brand requests", err);
                setLoading(false);
                setHasMore(false);
                setData([]);
            })

        } else if (userProfile?.partnerv2_id) {
            getSuitableBrandRequests(userProfile?.partnerv2_id).then((res) => {

                const newData = res?.data?.map((item: any) => {
                    const data = item?.discoveryRequest || item;
                    const status = item?.discoveryBrandPartner?.status && userProfile?.partnerv2_id == item?.discoveryBrandPartner?.partner_id ? item?.discoveryBrandPartner?.status : data?.status
                    const counts = item?.counts && item?.counts?.length > 0 ? item?.counts[0] : {}
                    return {
                        id: data?.id,
                        brand_name: data?.brand?.brand_name,
                        category: data?.category,
                        subCategory: data?.sub_category,
                        intParteners: counts?.interested_count || 0,
                        updatedAt: data?.updated_at,
                        createdAt: data?.created_at,
                        status: status,
                    }
                }) || []
                pageNumber == 1 ? setData(newData) : setData(prev => [...prev, ...newData]);
                setHasMore(false);
                setLoading(false);
            }).catch((err) => {
                console.log("Failed to fetch brand requests", err);
                setLoading(false);
                setHasMore(false);
                setData([]);
            })
        }
    };

    useEffect(() => {
        loadData(page);
    }, [page, userProfile]);

    const onViewDetails = (id: string) => {
        navigate(userProfile?.partnerv2_id ? `/partner-user/discovery/${id}` : `/brand-user/discovery/${id}`)
    }


    useEffect(() => {
        const updateScrollY = () => {
            if (tableRef.current) {
                const containerHeight = tableRef.current.clientHeight;
                const headerHeight = 48; // Adjust this value if your header is bigger/smaller
                setScrollY(containerHeight - headerHeight);
            }
        };

        updateScrollY();
        window.addEventListener('resize', updateScrollY);
        return () => window.removeEventListener('resize', updateScrollY);
    }, []);



    const handleScroll = (e: any) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        if (!loading && hasMore && scrollHeight - scrollTop <= clientHeight + 100) {
            setPage(prev => prev + 1);
        }
    };


    const columns: TableProps['columns'] = [
        {
            title: 'Brand Name',
            dataIndex: 'brand_name',
            key: 'brand_name',
            render: (brand: string, record: any) => (
                <DiscoverBrandItemCell data={record} />
            )
        },
        {
            title: 'Category & Sub-Category',
            dataIndex: 'subCategory',
            key: 'subCategory',
            render: (items: string[], record: any) => (
                <div>
                    <p className='mb-[6px] font-[500]'>{record?.category}</p>
                    {
                        items?.length > 2 ? (
                            <span>
                                {items?.slice(0, 2)?.map((item: string) => (
                                    <Tag bordered={false}>{item}</Tag>
                                ))}
                                <Popover
                                    trigger="hover"
                                    placement="bottom"
                                    content={(
                                        <Paragraph className='!mb-0 pl-[12px]'>
                                            <ul className="!mb-0">
                                                {items?.map((item: any, index: number) => (
                                                    <li className="text-[13px] font-[500] m-0" key={index}>
                                                        {item}
                                                    </li>
                                                ))
                                                }
                                            </ul>
                                        </Paragraph>
                                    )}  >
                                    <Button type="link" size="small" className="!px-0 leading-[12px] text-[12px] underline">+{items?.length - 2} more</Button>
                                </Popover>
                            </span>

                        ) : (
                            items?.map((item: string) => (
                                <Tag bordered={false}>{item}</Tag>
                            ))
                        )
                    }
                </div>

            )
        },
        {
            title: 'Int. Partners',
            dataIndex: 'intParteners',
            key: 'intParteners',
            className: "font-[500]",
            align: "center",
            width: 150,
            hidden: userProfile?.partnerv2_id,
            render: (value: number) => (
                <>{value?.toString().padStart(2, "0")}</>
            )
        },
        {
            title: 'Updated',
            dataIndex: 'updatedAt',
            key: 'updatedAt',
            className: "font-[500]",
            render: (value: string) => (
                <>{value ? dayjs(value).format(DATE_FORMAT_DD_MM_YYYY) : ""}</>
            )
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            hidden: userProfile?.partnerv2_id,
            render: (status: string) => {
                const statusText = status === STATUS_OPEN ? STATUS_PENDING : userProfile?.org_id && status === STATUS_INTEREST_EXPRESSED ? STATUS_INTEREST_RECEIVED : status;
                let color = "warning";
                if (statusText === STATUS_INTEREST_RECEIVED) {
                    color = "purple"
                } else if (statusText === STATUS_INTEREST_EXPRESSED || statusText === STATUS_PARTNER_INTEREST_EXPRESSED) {
                    color = "blue"
                } else if (statusText === STATUS_INTEREST_ACCEPTED || statusText === STATUS_PARTNER_INTEREST_ACCEPTED) {
                    color = "success"
                } else {
                    color = "warning"
                }

                return (statusText === STATUS_PENDING && userProfile?.partnerv2_id ? (
                    <></>
                ) : (<Tag
                    bordered={false}
                    className="capitalize !rounded-[24px] text-[10px] font-[500]"
                    color={color}
                >
                    {statusText?.replaceAll("_", " ")?.toLowerCase()}
                </Tag>
                )
                )
            }
        },
        {
            title: false,
            dataIndex: 'action',
            key: 'action',
            align: "end",
            render: (status: string, record: any) => (
                <Button
                    icon={<RightArrowIcon color='#667085' />}
                    type="text"
                    className="leading-[12px]"
                    onClick={() => onViewDetails(record?.id)}
                />
            )
        },
    ];


    return (
        <div
            ref={tableRef}
            className='h-[calc(100%-48px)] overflow-auto overflow-scroll-bar'
        >
            <Table
                dataSource={data}
                columns={columns}
                size="small"
                pagination={false}
                rowKey="key"
                loading={loading}
                scroll={{ y: scrollY, x: "max-content" }}
                className='overflow-scroll-bar'
                onScroll={(e) => {
                    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
                    if (!loading && hasMore && scrollHeight - scrollTop <= clientHeight + 100) {
                        setPage(prev => prev + 1);
                    }
                }}
            />
        </div>
    )
}

export default DiscoveryRequestsList